import { css } from "@emotion/css";
import { ArrowLeftIcon } from "@octopusdeploy/design-system-icons";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { ProjectStatusBar } from "~/areas/projects/components/ProjectStatus/ProjectStatusBar";
import { projectStatusActions } from "~/areas/projects/components/ProjectStatus/actions";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import type { ProjectCreatedOption } from "~/areas/projects/components/Projects/AddProject";
import AddProject, { ProjectInfrastructure } from "~/areas/projects/components/Projects/AddProject";
import { useDoBusyTaskEffect, type DoBusyTask } from "../DataBaseComponent";
import InternalLink from "../Navigation/InternalLink";
import { GuidedSetupFrame } from "../OnboardingDialog/GuidedSetupFrame";
import addProjectImageDark from "./assets/img-addproject-dark.svg";
import addProjectImageLight from "./assets/img-addproject-light.svg";
interface CreateNewProjectProps {
    spaceId: string;
    projectGroupId?: string;
    isOnboarding?: boolean;
    next: (project: ProjectResource, option: ProjectCreatedOption) => void;
    back: () => void;
    close: () => void;
    doBusyTask: DoBusyTask;
    projectStatus: ProjectStatus;
    k8sTargetAlreadyExistsInSpace: boolean;
    spaceHasEnvironments: boolean;
    hasProjectTemplates: boolean;
    projectTemplateSlug?: string;
}
function CreateNewProject(props: CreateNewProjectProps) {
    const onBackClicked = () => {
        props.back();
    };
    const onProjectCreated = (project: ProjectResource, option: ProjectCreatedOption) => {
        props.projectStatus.dispatch({
            type: projectStatusActions.newProjectFormSubmitted,
            intendsToUseK8s: option.infrastructure == ProjectInfrastructure.Kubernetes,
            k8sTargetAlreadyExistsInSpace: props.k8sTargetAlreadyExistsInSpace,
            intendsToUseConfigAsCode: option.vcsRedirect,
        });
        props.next(project, option);
    };
    useDoBusyTaskEffect(props.doBusyTask, async () => {
        props.projectStatus.dispatch({ type: projectStatusActions.projectInitiated, hasEnvironments: props.spaceHasEnvironments, hasProjectTemplates: props.hasProjectTemplates });
        // we only want this to run once, so we don't need to include the props in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const helpPanelContent = (<div>
            <div>
                A <strong>project</strong> represents a specific software component, service, or database that you want to deploy and manage. Each project has its own deployment process.
            </div>
        </div>);
    return (<GuidedSetupFrame helpPanelImage={{ src: addProjectImageLight, altText: "Add New Project", darkThemeSrc: addProjectImageDark }} helpPanelContent={helpPanelContent} onClose={props.close} showThemeSelector={props.isOnboarding}>
            <div className={createNewProjectStyles.projectStatusContainer}>
                {!props.isOnboarding && (<InternalLink className={createNewProjectStyles.projectsLink} to={links.projectsPage.generateUrl({ spaceId: props.spaceId })} onClick={props.close}>
                        <ArrowLeftIcon size={20}/>
                        <span>Projects</span>
                    </InternalLink>)}
                <ProjectStatusBar projectSlug={undefined} spaceId={props.spaceId} state={props.projectStatus.state} dispatch={props.projectStatus.dispatch} hideCloseButton/>
            </div>
            <div className={createNewProjectStyles.projectContainer}>
                <AddProject spaceId={props.spaceId} projectCreated={onProjectCreated} hideEnvironmentConfigWarning={true} projectNameTextBoxLabel="Project name" groupId={props.projectGroupId} title="Add New Project" saveButtonLabel={props.isOnboarding ? "Next" : "Create Project"} cancelButtonLabel={props.isOnboarding ? "Back" : "Cancel"} onCancelled={props.isOnboarding ? onBackClicked : props.close} isOnboarding={props.isOnboarding} projectTemplateSlug={props.projectTemplateSlug}/>
            </div>
        </GuidedSetupFrame>);
}
const createNewProjectStyles = {
    projectContainer: css({
        flexBasis: "100%",
        "& .MuiDialogContent-root, & .MuiDialogActions-root, & .MuiDialogTitle-root.MuiTypography-root div": {
            padding: "0 !important",
        },
        "& .MuiDialogContent-root div[role='alert'] + div": {
            marginTop: space[32],
        },
        "& .MuiDialogTitle-root.MuiTypography-root": {
            padding: `${space[48]} 0 ${space[32]} 0 !important`,
            "& .MuiLinearProgress-root[role='progressbar']": {
                display: "none",
            },
        },
    }),
    projectStatusContainer: css({
        paddingTop: space[40],
    }),
    projectsLink: css({
        paddingBottom: space[16],
        display: "flex",
        font: text.body.regular.small,
        gap: space[4],
    }),
};
export default CreateNewProject;
